import React from 'react';

import { TFunction } from '@lyearn/i18n';
import { CheckmarkCircleFill, Close, FileError, FileSheet } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { LinkButton, Typography } from '@lyearn/ui';
import { JobEntityStatusEnum } from '@/types/schema';
import { resetPadding } from '@/utils/styles';

import JobProgress from './SmallJobProgress';

interface ConfigType {
  Icon: typeof FileError;
  iconClassName?: string;
  rightNode?: React.ReactNode;
  descriptionKey: string;
  descriptionAction?: React.ReactNode;
}

interface RightNodeParams {
  isHovered: Params['isHovered'];
  onCancel: Params['onCancel'];
  progress: number;
}

const renderCancellableRightNode = (params: RightNodeParams) => {
  const { isHovered, onCancel, progress } = params;

  return isHovered ? (
    <LinkButton className={cn(resetPadding, 'py-2 px-4')} color={'internal'} onClick={onCancel}>
      <Close />
    </LinkButton>
  ) : (
    <JobProgress progress={progress} />
  );
};

interface Params {
  t: TFunction;
  isHovered: boolean;
  onCancel: () => void;
  onDownloadReport: () => void;
}

const getSmallJobStatusItemConfig = (params: Params): Record<JobEntityStatusEnum, ConfigType> => {
  const { t, isHovered, onCancel, onDownloadReport } = params;

  return {
    [JobEntityStatusEnum.NotStarted]: {
      descriptionKey: 'Common:BulkImport.Processing',
      Icon: FileSheet,
      rightNode: renderCancellableRightNode({ isHovered, onCancel, progress: 0 }),
    },
    [JobEntityStatusEnum.Waiting]: {
      descriptionKey: 'Common:BulkImport.Processing',
      Icon: FileSheet,
      rightNode: renderCancellableRightNode({ isHovered, onCancel, progress: 0 }),
    },
    [JobEntityStatusEnum.Parsing]: {
      descriptionKey: 'Common:BulkImport.Processing',
      Icon: FileSheet,
      rightNode: renderCancellableRightNode({ isHovered, onCancel, progress: 33.33 }),
    },
    [JobEntityStatusEnum.Validating]: {
      descriptionKey: 'Common:BulkImport.Processing',
      Icon: FileSheet,
      rightNode: renderCancellableRightNode({ isHovered, onCancel, progress: 33.33 }),
    },
    [JobEntityStatusEnum.InProcess]: {
      descriptionKey: 'Common:BulkImport.Processing',
      Icon: FileSheet,
      rightNode: <JobProgress progress={66.66} />,
    },
    [JobEntityStatusEnum.Completed]: {
      descriptionKey: 'Common:BulkImport.ImportCompleteDescription',
      Icon: FileSheet,
      rightNode: <CheckmarkCircleFill className="h-20 w-20" fillColorToken="icon-success" />,
    },
    [JobEntityStatusEnum.Cancelled]: {
      descriptionKey: 'Common:BulkImport.Canceled',
      Icon: FileSheet,
      iconClassName: 'grayscale',
    },
    [JobEntityStatusEnum.Failed]: {
      descriptionKey: 'Common:BulkImport.ImportFailed',
      Icon: FileError,
      descriptionAction: (
        <Typography
          className="flex-shrink-0"
          color="text-text-secondary"
          variant="body-short-03"
          onClick={onDownloadReport}>
          {t('Common:BulkImport.DownloadReport')}
        </Typography>
      ),
    },
  };
};

export default getSmallJobStatusItemConfig;
